import { useEffect, useState } from "react";

import { Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import { HighlightOffTwoTone as CloseIcon } from "@mui/icons-material";

import useAxios from "axios-hooks";

import TextField from "components/textField";
import Button from "components/button";
import Loader from "components/loader";
import Drawer from "components/drawer";
import ScrollableModal from "components/modals/scrollableModal";
import SearchableDropDown from "components/dropdowns/searchableDropDown";

import { getLocalStorage } from "features/components/helperFunctions";
import {
  footerCancelBtnStyle,
  footerGrid12Style,
  headerGrid12Style,
} from "features/products/index.styles";
import {
  AddEditReservationProps,
  ReservationDetailTypes,
} from "../../index.types";
import { reservationSlots } from "utils/constants";
import { API_END_POINTS } from "utils/constants/APIEndPoints";
import { dispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";

const AddEditReservations = ({
  branchesList,
  selectedReservation,
  addEditReservationsCallback,
  toggleAddEditReservations,
  handleToggleAddEditReservations,
  component,
}: AddEditReservationProps) => {
  const [reservationData, setReservationData] =
    useState<ReservationDetailTypes>({
      selectedBranch: { label: "", value: "" },
      fullName: "",
      totalGuests: "",
      phone: "",
      advancePayment: "",
      date: "",
      selectedTime: { label: "", value: "" },
      email: "",
      specialRequest: "",
    });

  const [fieldError, setFieldError] = useState<string>("");

  const { eatout_id, user_id } = getLocalStorage();

  //======================================= API Call Payloads =======================================//

  // add/edit reservations API payload
  const addEditReservationsAPIPayload = () => {
    const formData = new FormData();

    if (selectedReservation?.length) {
      formData.append("res_id", selectedReservation[0].reservation_id);
      formData.append("phone", reservationData.phone);
      formData.append("status", selectedReservation[0].status);
    } else {
      formData.append("contact_number", reservationData.phone);
    }

    formData.append("name", reservationData.fullName);
    formData.append("email", reservationData.email);
    formData.append("res_date", reservationData.date);
    formData.append("res_time", reservationData.selectedTime.label);
    formData.append("bid", reservationData.selectedBranch.value);
    formData.append("guest", reservationData.totalGuests);
    formData.append("request", reservationData.specialRequest);
    formData.append("advance", reservationData.advancePayment);

    formData.append("source", "biz");
    formData.append("uid", `${user_id}`);
    formData.append("rid", `${eatout_id}`);

    return formData;
  };

  // add/edit reservations API call
  const [
    { loading: addEditReservationsLoading, error: addEditReservationsError },
    addEditReservationsAPICall,
  ] = useAxios(
    selectedReservation?.length
      ? API_END_POINTS.editReservations
      : API_END_POINTS.addReservations,
    { manual: true }
  );

  //======================================= useEffect Hooks =======================================//

  useEffect(() => {
    if (selectedReservation?.length) {
      const filteredBranch = branchesList.filter(
        (branch) => branch.value === selectedReservation[0].bid
      );

      const filteredTime = reservationSlots.filter(
        (timeSlot) => timeSlot.label === selectedReservation[0].time
      );

      setReservationData({
        selectedBranch: filteredBranch.length
          ? filteredBranch[0]
          : { label: "", value: "" },
        fullName: selectedReservation[0].user_name,
        totalGuests: selectedReservation[0].guests,
        phone: selectedReservation[0].phone,
        advancePayment: selectedReservation[0].advance_payment,
        date: selectedReservation[0].date,
        selectedTime: filteredTime.length
          ? filteredTime[0]
          : {
              label: selectedReservation[0].time,
              value: selectedReservation[0].time,
            },
        email: selectedReservation[0].email,
        specialRequest: selectedReservation[0].request,
      });
    }
  }, [selectedReservation]);

  useEffect(() => {
    if (addEditReservationsError) {
      // notify user
      dispatch(
        openSnackbar({
          open: true,
          message: addEditReservationsError
            ? addEditReservationsError.message
            : "",
          alert: {
            color: "error",
          },
        })
      );
    }
  }, [addEditReservationsError]);

  //======================================= Handler Functions =======================================//

  const handleFieldChange = (e: {
    target: { value: string; name: string };
  }) => {
    const { name, value } = e.target;

    setReservationData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (fieldError) {
      setFieldError("");
    }
  };

  const handleBranchSelection = (
    event: React.ChangeEvent<{}>,
    value: any,
    name: string
  ) => {
    setReservationData((prevState) => ({
      ...prevState,
      selectedBranch: value ? value : { label: "", value: "" },
    }));

    if (fieldError) {
      setFieldError("");
    }
  };

  const handleTimeSelection = (
    event: React.ChangeEvent<{}>,
    value: any,
    name: string
  ) => {
    setReservationData((prevState) => ({
      ...prevState,
      selectedTime: value ? value : { label: "", value: "" },
    }));

    if (fieldError) {
      setFieldError("");
    }
  };

  const handleAddEditReservations = async () => {
    if (!reservationData.selectedBranch.label) {
      setFieldError("selectedBranch");
    } else if (!reservationData.fullName) {
      setFieldError("fullName");
    } else if (!reservationData.totalGuests) {
      setFieldError("totalGuests");
    } else if (Number(reservationData.totalGuests) < 1) {
      setFieldError("invalidGuests");
    } else if (!reservationData.phone) {
      setFieldError("phone");
    } else if (!reservationData.date) {
      setFieldError("date");
    } else if (!reservationData.selectedTime.label) {
      setFieldError("time");
    } else {
      // API call to add/edit reservation
      const { data } = await addEditReservationsAPICall({
        data: addEditReservationsAPIPayload(),
      });

      if (data?.status === "1") {
        addEditReservationsCallback();
      } else {
        if (data?.includes("You have already made a reservation")) {
          // notify user
          dispatch(
            openSnackbar({
              open: true,
              message: "You have already made a reservation!",
              alert: {
                color: "error",
              },
            })
          );
        } else {
          /**
           * case of reservation added successfully
           * sending false to stop getReservations API (calling from pusher)
           */
          addEditReservationsCallback(false);
        }
      }
    }
  };

  // return fields UI to add/edit reservations
  const reservationFields = () => {
    return (
      <>
        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", mb: "24px" }}>
            <SearchableDropDown
              label="Select Branch"
              value={reservationData.selectedBranch}
              options={branchesList}
              handleChange={handleBranchSelection}
              isError={fieldError === "selectedBranch" ? true : false}
              helperText={"Required*"}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", mb: "24px" }}>
            <TextField
              name="fullName"
              label="Full Name"
              value={reservationData.fullName}
              error={fieldError === "fullName" ? true : false}
              helperText={"Required*"}
              onChange={handleFieldChange}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", mb: "24px" }}>
            <Grid item xs={6}>
              <TextField
                name="totalGuests"
                type="number"
                label="No. of Guests"
                value={reservationData.totalGuests}
                error={
                  fieldError === "totalGuests" || fieldError === "invalidGuests"
                    ? true
                    : false
                }
                helperText={
                  fieldError === "totalGuests"
                    ? "Required*"
                    : fieldError === "invalidGuests"
                    ? "Invalid number"
                    : ""
                }
                onChange={handleFieldChange}
              />
            </Grid>

            <Grid item xs={6} sx={{ ml: "8px" }}>
              <TextField
                name="advancePayment"
                type="number"
                label="Advance Payment"
                InputProps={{ title: "" }}
                value={reservationData.advancePayment}
                onChange={handleFieldChange}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", mb: "24px" }}>
            <Grid item xs={6}>
              <TextField
                name="date"
                type="date"
                label="Date"
                value={reservationData.date}
                error={fieldError === "date" ? true : false}
                helperText={"Required*"}
                disablePrevDates={true}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={handleFieldChange}
              />
            </Grid>
            <Grid item xs={6} sx={{ ml: "8px" }}>
              <SearchableDropDown
                label="Time"
                value={reservationData.selectedTime}
                options={reservationSlots}
                handleChange={handleTimeSelection}
                isError={fieldError === "time" ? true : false}
                helperText={"Required*"}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", mb: "24px" }}>
            <Grid item xs={6}>
              <TextField
                name="phone"
                label="Phone"
                value={reservationData.phone}
                error={fieldError === "phone" ? true : false}
                helperText={"Required*"}
                onChange={handleFieldChange}
              />
            </Grid>

            <Grid item xs={6} sx={{ ml: "8px" }}>
              <TextField
                name="email"
                type="email"
                label="Email"
                value={reservationData.email}
                onChange={handleFieldChange}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", mb: "24px" }}>
            <Grid item xs={6}>
              <TextField
                name="tableNo"
                label="Table No."
                onChange={handleFieldChange}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", mb: "24px" }}>
            <TextField
              name="specialRequest"
              type="text"
              label="Special Request"
              value={reservationData.specialRequest}
              onChange={handleFieldChange}
              rows={4}
              multiline={true}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "unset !important",
                },
              }}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      {addEditReservationsLoading && <Loader />}

      {component === "Modal" ? (
        <ScrollableModal
          open={toggleAddEditReservations}
          onClose={handleToggleAddEditReservations}
        >
          <Grid container>
            <Grid item xs={12} sx={headerGrid12Style}>
              <Typography variant="h3" sx={{ color: "#212121" }}>
                {selectedReservation?.length
                  ? "Edit Reservation"
                  : "Add Reservation"}
              </Typography>

              <IconButton
                onClick={handleToggleAddEditReservations}
                sx={{ p: "unset" }}
              >
                <CloseIcon htmlColor="#D84315" fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>

          <Divider sx={{ m: "16px 40px 24px" }} />

          <Stack sx={{ p: "0 40px 40px" }}>
            {/* displays the fields to add reservations */}
            {reservationFields()}

            <Grid container>
              <Grid item xs={12} sx={footerGrid12Style}>
                <Button
                  variant={"contained"}
                  sx={footerCancelBtnStyle}
                  disabled={addEditReservationsLoading}
                  onClick={handleToggleAddEditReservations}
                >
                  Cancel
                </Button>

                <Button
                  variant={"contained"}
                  sx={{ p: "12px 24px", ml: "12px" }}
                  color={"secondary"}
                  disabled={addEditReservationsLoading}
                  onClick={handleAddEditReservations}
                >
                  {selectedReservation?.length
                    ? "Update Reservation"
                    : "Add Reservation"}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </ScrollableModal>
      ) : (
        <Drawer
          title={
            selectedReservation?.length ? "Edit Reservation" : "Add Reservation"
          }
          buttonText={
            selectedReservation?.length
              ? "Update Reservation"
              : "Add Reservation"
          }
          open={toggleAddEditReservations}
          onClose={handleToggleAddEditReservations}
          handleCancelClick={handleToggleAddEditReservations}
          onClick={handleAddEditReservations}
        >
          <Stack sx={{ p: "32px 25px 0px" }}>
            {/* displays the fields to add reservation */}
            {reservationFields()}
          </Stack>
        </Drawer>
      )}
    </>
  );
};

export default AddEditReservations;
